import { Link, NavigateOptions, useNavigate, useRouter, useSearch } from '@tanstack/react-router'
import {
	RowClickedEvent,
	SortChangedEvent,
	themeQuartz,
	type CellClassParams,
	type ColDef,
	type ColTypeDef,
	type GetRowIdParams,
	type ICellRendererParams
} from 'ag-grid-community'
import { AgGridReact, AgGridReactProps } from 'ag-grid-react'
import { useMemo, useState, useCallback, type ComponentType } from 'react'

import { Badge } from '@/components/base/badge'
import { IconButton } from '@/components/base/icon-button'
import { useIsMobile } from '@/hooks/use-is-mobile'
import { useI18n } from '@/i18n/i18n-react'
import { ChevronDown } from '@/icons/chevron-down'
import { ChevronRight } from '@/icons/chevron-right'
import { clx } from '@/utils/clx'

const theme = themeQuartz.withParams({
	fontFamily: 'var(--font-family-sans)',
	fontSize: '0.8125rem',
	textColor: 'var(--fg-subtle)',
	headerFontWeight: '500',
	headerHeight: '48px',
	headerBackgroundColor: 'var(--bg-subtle)',
	rowHoverColor: 'var(--bg-base-hover)',
	wrapperBorderRadius: 0,
	wrapperBorder: false
})

type RowData<TData> = TData & { __isDetailRow?: boolean; __masterData?: TData }

// When `rowToRouterConfig` is set, `Table` wraps each cell's content in a real
// `<Link>` so rows are true anchors (Cmd/Ctrl-click, right-click "open in new
// tab", intent-based preload). Columns that render their own interactive
// controls (buttons, switches, nested links) opt out since anchors can't
// contain other focusable/interactive elements.
export type TableColDef<TData> = ColDef<TData> & { suppressRowLink?: boolean }

export type SortDirection = 'asc' | 'desc'

// Reads the current sort from the route's search params and returns a
// `ServerSortConfig` that writes the chosen column/direction back to the URL
// (resetting pagination), which drives a re-query. `sortableColumns` are the
// column ids the backing API supports sorting by.
export function useServerSort(sortableColumns: string[]): ServerSortConfig {
	const navigate = useNavigate()
	const search = useSearch({ strict: false }) as { sortBy?: string; sortOrder?: SortDirection }
	return {
		sortBy: search.sortBy,
		sortOrder: search.sortOrder,
		sortableColumns,
		onChange: ({ sortBy, sortOrder }) => {
			navigate({ to: '.', search: (prev: any) => ({ ...prev, sortBy, sortOrder, offset: 0 }) })
		}
	}
}

// Server-side sorting: the grid only holds one page of data, so client-side
// sorting would only reorder the current page. When `serverSort` is provided the
// grid becomes a controlled, click-to-sort surface that pushes the chosen column
// and direction up to the caller (which re-queries the API), while its own row
// comparator is neutered so it never reorders the server-provided page.
export type ServerSortConfig = {
	sortBy?: string
	sortOrder?: SortDirection
	// Column ids (matching `field`/`colId`) that the API can sort by. Other
	// columns are rendered non-sortable to avoid misleading per-page sorting.
	sortableColumns: string[]
	onChange: (next: { sortBy?: string; sortOrder?: SortDirection }) => void
}

type TableProps<TData> = {
	data?: TData[] | null
	columnUtilities?: ColumnUtilities<TData>
	columnDefs: TableColDef<TData>[]
	rowToRouterConfig?: (row: TData) => NavigateOptions | undefined
	getRowId?: (data: TData) => string
	serverSort?: ServerSortConfig
	// Master-detail props
	masterDetail?: boolean
	detailRenderer?: (data: TData) => React.ReactNode
	isRowMaster?: (data: TData) => boolean
	detailPanelRowHeight?: number | ((data: TData) => number)
	// When true, the leading expand arrow column is prepended and row clicks
	// toggle the master-detail panel instead of navigating. When false/omitted,
	// all other master-detail wiring (state, detail renderer, full-width row) is
	// kept intact but rows navigate instead of expanding.
	enableExpansion?: boolean
	// Empty state
	emptyState?: React.ReactNode
} & Omit<AgGridReactProps<TData>, 'getRowId'>

export function Table<TData extends Record<string, any>>({
	data,
	columnUtilities,
	columnDefs,
	rowToRouterConfig,
	getRowId,
	serverSort,
	masterDetail,
	detailRenderer,
	isRowMaster,
	detailPanelRowHeight,
	enableExpansion,
	emptyState,
	...props
}: TableProps<TData>) {
	const columnTypes = useColumnTypes<TData>(columnUtilities)
	const router = useRouter()
	const [expandedRowIds, setExpandedRowIds] = useState<Set<string>>(new Set())
	const isMobile = useIsMobile()

	// Disable master-detail on mobile
	const masterDetailEnabled = masterDetail && !isMobile

	const toggleRowExpansion = useCallback(
		(rowData: TData) => {
			const rowId = getRowId?.(rowData) ?? JSON.stringify(rowData)
			setExpandedRowIds(prev => {
				const next = new Set(prev)
				if (next.has(rowId)) {
					next.delete(rowId)
				} else {
					next.add(rowId)
				}
				return next
			})
		},
		[getRowId]
	)

	// Transform data to include detail rows
	const enhancedData = useMemo<RowData<TData>[]>(() => {
		if (!masterDetailEnabled || !detailRenderer || !data) {
			return (data ?? []) as RowData<TData>[]
		}

		const result: RowData<TData>[] = []
		data.forEach(row => {
			result.push(row as RowData<TData>)

			const rowId = getRowId?.(row) ?? JSON.stringify(row)
			const canExpand = !isRowMaster || isRowMaster(row)
			if (canExpand && expandedRowIds.has(rowId)) {
				result.push({
					...({} as TData),
					__isDetailRow: true,
					__masterData: row
				} as RowData<TData>)
			}
		})
		return result
	}, [data, masterDetailEnabled, detailRenderer, expandedRowIds, getRowId, isRowMaster])

	// Add expand/collapse column if master-detail is enabled
	const enhancedColumnDefs = useMemo(() => {
		if (!masterDetailEnabled || !detailRenderer || !enableExpansion) {
			return columnDefs
		}

		const expandColumn: TableColDef<RowData<TData>> = {
			headerName: '',
			field: '__expand__' as any,
			width: 40,
			minWidth: 40,
			maxWidth: 40,
			resizable: false,
			sortable: false,
			suppressMovable: true,
			suppressRowLink: true,
			cellClass: '!px-0',
			cellRenderer: ({ data }: any) => {
				if (!data || data.__isDetailRow) {
					return null
				}
				const canExpand = !isRowMaster || isRowMaster(data)
				if (!canExpand) {
					return null
				}

				const rowId = getRowId?.(data) ?? JSON.stringify(data)
				const isExpanded = expandedRowIds.has(rowId)

				return (
					<div className='flex h-full w-full items-center justify-center'>
						{isExpanded ? <ChevronDown className='h-4 w-4' /> : <ChevronRight className='h-4 w-4' />}
					</div>
				)
			}
		}

		return [expandColumn, ...columnDefs]
	}, [masterDetailEnabled, detailRenderer, columnDefs, expandedRowIds, isRowMaster, getRowId, enableExpansion])

	// Apply the controlled server-sort state to the columns: reflect the active
	// sort in the header, only allow sorting on server-supported columns, and use
	// a no-op comparator so AG Grid never reorders the (already sorted) page.
	const sortedColumnDefs = useMemo(() => {
		if (!serverSort) {
			return enhancedColumnDefs
		}
		const noop = () => 0
		return enhancedColumnDefs.map(col => {
			const colId = (col.colId ?? col.field) as string | undefined
			if (!colId || !serverSort.sortableColumns.includes(colId)) {
				return { ...col, sortable: false }
			}
			return {
				...col,
				sortable: true,
				comparator: noop,
				sort: colId === serverSort.sortBy ? (serverSort.sortOrder ?? null) : null
			}
		})
	}, [enhancedColumnDefs, serverSort])

	const handleSortChanged = useCallback(
		(event: SortChangedEvent<RowData<TData>>) => {
			if (!serverSort || event.source !== 'uiColumnSorted') {
				return
			}
			const sorted = event.api.getColumnState().find(state => !!state.sort)
			if (sorted?.colId && sorted.sort) {
				serverSort.onChange({ sortBy: sorted.colId, sortOrder: sorted.sort as SortDirection })
			} else {
				serverSort.onChange({ sortBy: undefined, sortOrder: undefined })
			}
		},
		[serverSort]
	)

	// Resolve the renderer AG Grid would actually use for a column: an explicit
	// `cellRenderer` wins, otherwise fall back to the renderer contributed by
	// `columnTypes` (e.g. the `badge` type). `date`/`currency` only set a
	// `valueFormatter`, which AG Grid computes into `valueFormatted` regardless
	// of which cellRenderer runs, so no special-casing is needed for those.
	const resolveInnerRenderer = useCallback(
		(col: TableColDef<RowData<TData>>) => {
			if (col.cellRenderer) {
				return col.cellRenderer as ComponentType<ICellRendererParams> | string
			}
			const types = col.type ? (Array.isArray(col.type) ? col.type : [col.type]) : []
			for (const type of types) {
				const renderer = (columnTypes as Record<string, ColTypeDef>)[type]?.cellRenderer
				if (renderer) {
					return renderer as ComponentType<ICellRendererParams> | string
				}
			}
			return undefined
		},
		[columnTypes]
	)

	// When rows are navigable, wrap each cell's (resolved) content in a real
	// `<Link>` so the whole row behaves like a native anchor: Cmd/Ctrl/middle
	// click opens a background tab, right-click offers "open in new tab", and
	// TanStack Router's `defaultPreload: 'intent'` preloads on hover for free.
	// Columns rendering their own interactive controls opt out via
	// `suppressRowLink` since anchors can't contain other interactive elements.
	const linkedColumnDefs = useMemo(() => {
		if (!rowToRouterConfig) {
			return sortedColumnDefs
		}
		return sortedColumnDefs.map(colDef => {
			const col = colDef as TableColDef<RowData<TData>>
			if (col.suppressRowLink) {
				return col
			}
			const Inner = resolveInnerRenderer(col)
			if (typeof Inner === 'string') {
				// AG Grid built-in renderer (referenced by name, e.g. a checkbox) --
				// can't safely wrap in a link, so leave the column as-is.
				return col
			}
			return {
				...col,
				cellClass: (params: CellClassParams<RowData<TData>>) => {
					const base = typeof col.cellClass === 'function' ? col.cellClass(params as any) : col.cellClass
					return clx(base as string | string[] | undefined, '!px-0')
				},
				cellRenderer: (params: ICellRendererParams<RowData<TData>>) => {
					const content = Inner ? <Inner {...(params as any)} /> : (params.valueFormatted ?? params.value)
					const to = params.data && !params.data.__isDetailRow ? rowToRouterConfig(params.data) : undefined
					const className = 'flex h-full w-full items-center px-[var(--ag-cell-horizontal-padding)]'
					if (!to) {
						return <div className={className}>{content}</div>
					}
					return (
						<Link {...(to as any)} className={className} tabIndex={-1}>
							{content}
						</Link>
					)
				}
			}
		})
	}, [sortedColumnDefs, rowToRouterConfig, resolveInnerRenderer])

	const handleRowClicked = (event: RowClickedEvent<RowData<TData>>) => {
		// If detail row, do nothing
		if (event.data?.__isDetailRow) {
			return
		}

		if (!event.data) {
			return
		}

		// AG Grid attaches its row-click listener on the native DOM cell, which
		// fires before the click bubbles to React's root. That means calling
		// `stopPropagation()` inside a React cell renderer can't prevent this
		// handler from running. Instead, detect clicks that originate from an
		// interactive control (buttons, links, form fields) or an element that
		// explicitly opts out, and skip navigation for those.
		const target = event.event?.target as HTMLElement | null
		if (target?.closest('button, a, input, select, textarea, label, [data-no-row-nav]')) {
			return
		}

		// If master-detail enabled (desktop only) and expansion is explicitly
		// enabled, toggle the panel. Otherwise fall through to the navigation branch.
		if (masterDetailEnabled && enableExpansion) {
			const canExpand = !isRowMaster || isRowMaster(event.data)
			if (canExpand) {
				toggleRowExpansion(event.data)
				return
			}
		}

		// Otherwise navigate if configured (always navigate on mobile)
		if (!rowToRouterConfig || !enableRowNavigation) {
			return
		}
		const navigateOptions = rowToRouterConfig(event.data)
		if (!navigateOptions) {
			return
		}
		router.navigate(navigateOptions)
	}

	const fullWidthCellRenderer = useCallback(
		({ data }: any) => {
			if (!data?.__isDetailRow || !detailRenderer || !data.__masterData) {
				return null
			}
			return (
				<div className='animate-in fade-in slide-in-from-top-2 border-t border-ui-border-base bg-ui-bg-subtle px-4 py-3 duration-150 sm:px-6 sm:py-4'>
					{detailRenderer(data.__masterData)}
				</div>
			)
		},
		[detailRenderer]
	)

	const isFullWidthRow = useCallback((params: any) => {
		return params.rowNode.data?.__isDetailRow === true
	}, [])

	const getRowIdFunc = useCallback(
		(params: GetRowIdParams<RowData<TData>>) => {
			if (params.data.__isDetailRow) {
				const masterRowId = getRowId?.(params.data.__masterData!) ?? JSON.stringify(params.data.__masterData)
				return `detail-${masterRowId}`
			}
			return getRowId?.(params.data) ?? JSON.stringify(params.data)
		},
		[getRowId]
	)

	const enableRowNavigation = !!rowToRouterConfig
	const enableRowClick = enableRowNavigation || masterDetailEnabled

	// Show custom empty state if data is empty and emptyState is provided
	if (emptyState && (!data || data.length === 0)) {
		return <>{emptyState}</>
	}

	return (
		<AgGridReact<RowData<TData>>
			className='txt-compact-small'
			rowData={enhancedData}
			columnDefs={linkedColumnDefs}
			columnTypes={columnTypes as any}
			rowHeight={48}
			theme={theme}
			onSortChanged={serverSort ? handleSortChanged : props.onSortChanged}
			onRowClicked={enableRowClick ? handleRowClicked : props.onRowClicked}
			onCellMouseOver={props.onCellMouseOver}
			rowClass={enableRowClick ? 'cursor-pointer' : undefined}
			getRowId={getRowIdFunc}
			isFullWidthRow={masterDetailEnabled ? isFullWidthRow : undefined}
			fullWidthCellRenderer={masterDetailEnabled ? fullWidthCellRenderer : undefined}
			getRowHeight={
				masterDetailEnabled
					? (params: any) => {
							if (params.data?.__isDetailRow) {
								if (typeof detailPanelRowHeight === 'function') {
									return detailPanelRowHeight(params.data.__masterData)
								}
								return detailPanelRowHeight ?? 280
							}
							return 48
						}
					: undefined
			}
			{...props}
		/>
	)
}

export type ColumnUtilities<TData> = {
	getCurrencyCode?: (data: TData) => string
}

const useColumnTypes = <TData,>(utilities?: ColumnUtilities<TData>) => {
	const { L } = useI18n()
	const { getCurrencyCode } = utilities || {}
	return useMemo<Record<string, ColTypeDef>>(
		() => ({
			date: {
				valueFormatter: ({ value }) => (!!value ? L.Date.DateAndTime(value) : '-')
				// filter: 'agDateColumnFilter'
			},
			currency: {
				valueFormatter: ({ value, data }) => {
					if (typeof value === 'string') {
						return value
					}
					return L.Amount.Default({
						amount: value,
						currencyCode: getCurrencyCode?.(data) ?? ''
					})
				}
			},
			badge: {
				cellRenderer: ({ value }) => {
					if (!value) {
						return null
					}
					if (Array.isArray(value)) {
						return (
							<div className='flex h-full w-full items-center gap-1 overflow-hidden'>
								{value.map(v => (
									<Badge key={v} size='2xsmall'>
										{v}
									</Badge>
								))}
							</div>
						)
					}
					return <Badge size='2xsmall'>{value}</Badge>
				}
			}
			// image: {
			// 	resizable: false,
			// 	sortable: false,
			// 	headerComponent: () => null,
			// 	cellClass: '!p-0',
			// 	width: 48,
			// 	minWidth: 48,
			// 	maxWidth: 48,
			// 	cellRenderer: ({ value }) => {
			// 		if (!value) {
			// 			return null
			// 		}
			// 		return (
			// 			<div className='flex h-full w-full items-center overflow-hidden'>
			// 				<img src={value} alt='Image' className='aspect-square h-full object-contain' />
			// 			</div>
			// 		)
			// 	}
			// }
		}),
		[]
	)
}

export type ActionButton<TData> = {
	icon: React.ReactNode
	onClick: (data: TData) => void
}

export const createActionsColumn = <TData,>(buttons: ActionButton<TData>[]): TableColDef<TData> => {
	return {
		resizable: false,
		width: 30 + buttons.length * 32 + (buttons.length - 1) * 8,
		sortable: false,
		suppressRowLink: true,
		cellRenderer: ({ data }) => (
			<div className='flex h-full w-full items-center gap-x-2'>
				{buttons.map((button, index) => (
					<IconButton key={index} onClick={() => button.onClick(data)}>
						{button.icon}
					</IconButton>
				))}
			</div>
		)
	}
}
